import { useI18n } from 'hooks/useI18n';

import {
  Avatar,
  Badge,
  BadgeList,
  ButtonLink,
  Card,
  Header,
  Stack,
  StackItem
} from '@asgard/ui-library';
import { faMedal, faThumbsUp } from '@fortawesome/pro-solid-svg-icons';
import { HashAnchor } from 'components/HashAnchor';
import { PageContentContainer } from 'components/layout/Page.styled';
import HtmlTrans from 'components/shared/HtmlTrans';
import { getRandomColorBySeed } from 'utils/colors';
import { YourOfferSalaryCtaStyles } from './YourOfferSalaryCta.styles';
import { YourOfferSalaryCtaProps } from './YourOfferSalaryCta.types';

export const YourOfferSalaryCta: React.FC<YourOfferSalaryCtaProps> = ({
  data
}) => {
  const { t } = useI18n('your-offer-salary');

  if (
    !data.hasEnoughManfredites ||
    !data.stats ||
    data.stats.manfredites.length <= 0
  ) {
    return null;
  }

  const manfrediteNames = [
    'Bremer dan Gorst',
    'Whirrun de Blight',
    'Curdnen Craw'
  ];

  return (
    <YourOfferSalaryCtaStyles.Root>
      <PageContentContainer containerSize="lg">
        <HashAnchor name="candidatos" />
        <Header as="h2" size="4xl" align="center" editorial>
          <HtmlTrans
            i18nKey="your-offer-salary.cta.title"
            ns="your-offer-salary"
          />
        </Header>

        <Stack direction="column" gap="s">
          {data.stats.manfredites.map((candidate, index) => (
            <Card hideImage key={`candidate_${index}`} className="card">
              <ButtonLink
                href="#yourOfferSalaryForm"
                size="xs"
                variant="ghost"
                schema="positive"
                startIcon={faThumbsUp}
                label={t('your-offer-salary.cta.button')}
                className="like"
              />

              <Stack direction="column" gap="m">
                <YourOfferSalaryCtaStyles.AvatarContainer>
                  <StackItem className="blur">
                    <Avatar
                      size="2xl"
                      img={{
                        src: `/images/manfredita-${index + 1}.webp`,
                        alt: `${manfrediteNames[index]}`
                      }}
                    />
                  </StackItem>

                  <Header as="h3" size="xl" className="blur">
                    {manfrediteNames[index]}
                  </Header>
                </YourOfferSalaryCtaStyles.AvatarContainer>

                <Stack direction="column" gap="xl">
                  {candidate.roles.length > 0 && (
                    <Stack gap="s">
                      <strong>
                        {t('your-offer-salary.manfredite-info-card.roles')}
                      </strong>

                      <BadgeList
                        stackWrapperProps={{
                          className: 'badgeList'
                        }}
                        items={candidate.roles.map((role) => ({
                          key: `role_${role}`,
                          label: role,
                          variant: 'ghost',
                          schema: 'neutral',
                          size: 'xs',
                          bold: false,
                          color: getRandomColorBySeed(role)
                        }))}
                      />
                    </Stack>
                  )}

                  {candidate.salaryRange && (
                    <Stack wrap gap="s">
                      <strong>
                        {t('your-offer-salary.manfredite-info-card.salary')}
                      </strong>

                      <StackItem>
                        <Badge
                          label={candidate.salaryRange}
                          variant="ghost"
                          schema={'neutral'}
                          size={'xs'}
                          color={getRandomColorBySeed(candidate.salaryRange)}
                        />
                      </StackItem>
                    </Stack>
                  )}

                  {candidate.experienceRange && (
                    <Stack wrap gap="s">
                      <strong>
                        {t('your-offer-salary.manfredite-info-card.experience')}
                      </strong>

                      <StackItem>
                        <Badge
                          label={`${candidate.experienceRange} ${t(
                            'your-offer-salary.manfredite-info-card.years'
                          )}`}
                          variant="ghost"
                          schema={'neutral'}
                          size={'xs'}
                          color={getRandomColorBySeed(
                            candidate.experienceRange
                          )}
                        />
                      </StackItem>
                    </Stack>
                  )}

                  {(candidate.techs.length > 0 ||
                    candidate.mainTechs.length > 0) && (
                    <Stack gap="s">
                      <strong>
                        {t('your-offer-salary.manfredite-info-card.techs')}
                      </strong>

                      <Stack direction="column" gap="m">
                        <BadgeList
                          stackWrapperProps={{
                            className: 'badgeList'
                          }}
                          items={candidate.mainTechs.map((tech) => ({
                            key: `mainTech_${tech}`,
                            icon: faMedal,
                            label: tech,
                            variant: 'ghost',
                            schema: 'active',
                            size: 'xs',
                            bold: true,
                            color: getRandomColorBySeed(tech),
                            tooltip: t(
                              `your-offer-salary.manfredite-info-card.main-tech`
                            )
                          }))}
                        />

                        <BadgeList
                          stackWrapperProps={{
                            className: 'badgeList'
                          }}
                          items={candidate.techs.map((tech) => ({
                            key: `tech_${tech}`,
                            label: tech,
                            variant: 'ghost',
                            schema: 'neutral',
                            size: 'xs',
                            color: getRandomColorBySeed(tech)
                          }))}
                        />
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>
      </PageContentContainer>
    </YourOfferSalaryCtaStyles.Root>
  );
};
